import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { PageTitle, Panel } from "@/pages/admin/AdminLayout";

const COLS = [["view", "Visitas"], ["media", "Fotos / vídeos"], ["ai_shown", "Mostrado pela IA"], ["whatsapp", "Cliques WhatsApp"], ["request", "Pedidos"]];
const STATUS = { published: "Publicado", reserved: "Reservado", sold: "Vendido", draft: "Rascunho" };
const shortDay = (d) => d.slice(8, 10) + "/" + d.slice(5, 7);

function Kpis({ totals }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      {COLS.map(([k, l]) => <Panel key={k}><p className="text-[11px] uppercase tracking-[0.2em] text-[#A39E93]">{l}</p><p className="mt-2 text-3xl font-display text-[#F3EFE6]" data-testid={`interest-total-${k}`}>{totals[k]}</p></Panel>)}
    </div>
  );
}

function Chart({ series }) {
  return (
    <Panel className="mb-6" data-testid="interest-chart">
      <p className="text-sm mb-4">Visitantes únicos às páginas dos modelos, por dia</p>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ left: -20, right: 8, top: 4 }}>
            <defs><linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C8AE6E" stopOpacity={0.35} /><stop offset="100%" stopColor="#C8AE6E" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid stroke="#282520" vertical={false} />
            <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fill: "#A39E93", fontSize: 11 }} stroke="#282520" minTickGap={18} />
            <YAxis allowDecimals={false} tick={{ fill: "#A39E93", fontSize: 11 }} stroke="#282520" />
            <Tooltip contentStyle={{ background: "#141310", border: "1px solid #282520", fontSize: 12 }} labelFormatter={shortDay} formatter={(v) => [v, "Visitantes"]} />
            <Area type="monotone" dataKey="views" stroke="#C8AE6E" strokeWidth={1.5} fill="url(#gv)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

function Ranking({ rows }) {
  const max = Math.max(1, ...rows.map((x) => x.score));
  if (!rows.length) return <Panel><p className="text-sm text-[#A39E93]" data-testid="interest-empty">Ainda sem visitas neste período.</p></Panel>;
  return (
    <Panel className="overflow-x-auto" data-testid="interest-ranking">
      <table className="w-full text-sm min-w-[760px]">
        <thead><tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#A39E93]"><th className="pb-3 w-8">#</th><th className="pb-3">Modelo</th>{COLS.map(([k, l]) => <th key={k} className="pb-3 text-right">{l}</th>)}<th className="pb-3 pl-4 w-40">Interesse</th></tr></thead>
        <tbody>
          {rows.map((x, i) => (
            <tr key={x.watch_id} className="border-t border-[#282520]" data-testid={`interest-row-${x.slug}`}>
              <td className="py-3 text-[#A39E93]">{i + 1}</td>
              <td className="py-3"><div className="flex items-center gap-3">{x.thumb && <img src={x.thumb} alt="" className="w-10 h-10 object-cover" />}<div><Link to={`/admin/relogios/${x.watch_id}`} className="text-[#E6D5AC] hover:underline">{x.label}</Link><p className="text-[11px] text-[#A39E93]">{STATUS[x.status] || x.status}</p></div></div></td>
              {COLS.map(([k]) => <td key={k} className="py-3 text-right tabular-nums" data-testid={`interest-${x.slug}-${k}`}>{x[k]}</td>)}
              <td className="py-3 pl-4"><div className="flex items-center gap-3"><div className="h-1.5 flex-1 bg-[#282520]"><div className="h-full bg-[#C8AE6E]" style={{ width: `${(x.score / max) * 100}%` }} /></div><span className="tabular-nums text-xs text-[#E6D5AC] w-8 text-right" data-testid={`interest-${x.slug}-score`}>{x.score}</span></div></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-[#A39E93] mt-4">Interesse = visitas + 2× fotos/vídeos + mostrado pela IA + 5× cliques WhatsApp + 8× pedidos. Cada visitante conta uma vez por dia em cada modelo.</p>
    </Panel>
  );
}

export default function Interest() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { let live = true; setD(null); setErr(false); api.get(`/admin/stats/interest?days=${days}`).then((x) => live && setD(x.data)).catch(() => live && setErr(true)); return () => { live = false; }; }, [days]);
  return (
    <div data-testid="admin-interest">
      <PageTitle title="Interesse nos modelos" sub="Que relógios despertam mais interesse: visitas, fotos e vídeos abertos, sugestões do consultor IA, cliques em WhatsApp e pedidos." />
      <div className="flex gap-2 mb-6">{[7, 30, 90].map((n) => <button key={n} onClick={() => setDays(n)} className={days === n ? "btn-gold !py-1.5 !px-4 text-xs" : "btn-line !py-1.5 !px-4 text-xs"} data-testid={`interest-days-${n}`}>{n} dias</button>)}</div>
      {!d && !err && <p className="text-sm text-[#A39E93] animate-pulse" data-testid="interest-loading">A carregar…</p>}
      {err && <p className="text-sm text-[#e08f7f]" data-testid="interest-error">Não foi possível carregar as estatísticas. Tente novamente.</p>}
      {d && <><Kpis totals={d.totals} /><Chart series={d.series} /><Ranking rows={d.ranking} /></>}
    </div>
  );
}
